import { useState } from 'react';
import { AdminRole, AdminScreen, SaveAdminRoleRequest } from '../../types/admin-roles';
import RoleFormDialog from './RoleFormDialog';
import TypedConfirmDialog from '../TypedConfirmDialog';

interface RolesTabProps {
  roles: AdminRole[];
  screens: AdminScreen[];
  onCreate: (body: SaveAdminRoleRequest) => Promise<void>;
  onUpdate: (roleId: string, body: SaveAdminRoleRequest) => Promise<void>;
  onDelete: (roleId: string) => Promise<void>;
}

export default function RolesTab({ roles, screens, onCreate, onUpdate, onDelete }: RolesTabProps) {
  const [editing, setEditing] = useState<AdminRole | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<AdminRole | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const labelFor = (key: string) => screens.find((s) => s.key === key)?.label ?? key;

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteError(null);
    try {
      await onDelete(deleting.id);
      setDeleting(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Could not delete the role');
    }
  };

  return (
    <>
      <div className="admin-card">
        <div className="admin-card-header admin-card-header-actions">
          <p className="admin-text-muted">
            A role is a named set of screens. Changes reach its members within a minute.
          </p>
          <button type="button" className="admin-btn admin-btn-primary" onClick={() => setCreating(true)}>
            Create role
          </button>
        </div>

        {roles.length === 0 ? (
          <div className="admin-empty">No roles yet. Create one to start granting screen access.</div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Screens</th>
                  <th>Members</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => (
                  <tr key={role.id}>
                    <td>
                      <div className="admin-roles-checkbox-label">
                        {role.name}
                        {role.builtIn && <span className="admin-badge admin-badge-role">Built-in</span>}
                      </div>
                      {role.description && <div className="admin-text-muted">{role.description}</div>}
                    </td>
                    <td>
                      {role.screenKeys.length === 0 ? (
                        <span className="admin-text-muted">None</span>
                      ) : (
                        role.screenKeys.map((key) => (
                          <span key={key} className="admin-badge admin-badge-role">{labelFor(key)}</span>
                        ))
                      )}
                    </td>
                    <td>{role.memberCount}</td>
                    <td className="admin-table-actions">
                      <button type="button" className="admin-btn" onClick={() => setEditing(role)}>Edit</button>
                      <button
                        type="button"
                        className="admin-btn"
                        onClick={() => { setDeleteError(null); setDeleting(role); }}
                        disabled={role.builtIn || role.memberCount > 0}
                        title={
                          role.builtIn
                            ? 'Built-in roles cannot be deleted'
                            : role.memberCount > 0
                              ? 'Unassign this role from its members first'
                              : undefined
                        }
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating && (
        <RoleFormDialog
          screens={screens}
          role={null}
          onSave={onCreate}
          onClose={() => setCreating(false)}
        />
      )}

      {editing && (
        <RoleFormDialog
          screens={screens}
          role={editing}
          onSave={(body) => onUpdate(editing.id, body)}
          onClose={() => setEditing(null)}
        />
      )}

      {deleting && (
        <TypedConfirmDialog
          title={`Delete ${deleting.name}?`}
          message={
            deleteError
              ?? 'This role will no longer be assignable. Admins who held it keep any screens granted by their other roles.'
          }
          confirmPhrase="DELETE"
          confirmLabel="Delete role"
          danger
          onConfirm={confirmDelete}
          onCancel={() => { setDeleteError(null); setDeleting(null); }}
        />
      )}
    </>
  );
}
